<template>
    <div class="payment-form">
        <div v-if="showMainSections" class="payment-form__body">
            <p class="payment-form__legend">* Pole obowiązkowe</p>

            <section
                v-if="showAttendanceSection"
                class="payment-form__section payment-form__section--attendance"
            >
                <h3 class="payment-form__section-title">Forma udziału</h3>
                <div class="payment-form__options">
                    <label
                        class="payment-form__option"
                        :class="{
                            'payment-form__option--disabled': available_attendance_type === 'online',
                            'payment-form__option--active': attendanceType === 'stationary',
                        }"
                    >
                        <input
                            v-model="attendanceType"
                            class="payment-form__radio"
                            type="radio"
                            value="stationary"
                            :disabled="available_attendance_type === 'online'"
                        >
                        <span class="payment-form__option-label">Udział stacjonarny</span>
                    </label>
                    <label
                        class="payment-form__option"
                        :class="{
                            'payment-form__option--disabled': available_attendance_type === 'stationary',
                            'payment-form__option--active': attendanceType === 'online',
                        }"
                    >
                        <input
                            v-model="attendanceType"
                            class="payment-form__radio"
                            type="radio"
                            value="online"
                            :disabled="available_attendance_type === 'stationary'"
                        >
                        <span class="payment-form__option-label">Udział online</span>
                    </label>
                </div>
                <p v-if="fieldErrors.attendanceType" class="payment-form__error">
                    {{ fieldErrors.attendanceType }}
                </p>
            </section>

            <section
                v-if="hasEventableBenefits"
                class="payment-form__section payment-form__section--benefits"
            >
                <div
                    v-for="element in visibleEventableBenefitElements()"
                    :key="element.code"
                    class="payment-form__benefit"
                >
                    <h4 v-if="element.label" class="payment-form__benefit-title">
                        <span v-html="element.label"></span>
                        <span v-if="isEventableElementRequired(element)" class="payment-form__required">*</span>
                    </h4>

                    <div v-if="element.element_type === 'checkbox'" class="payment-form__benefit-body">
                        <label
                            class="payment-form__option"
                            :class="{ 'payment-form__option--disabled': isCheckboxOptionDisabled(element) }"
                        >
                            <input
                                class="payment-form__checkbox"
                                type="checkbox"
                                :checked="isBenefitChecked(element)"
                                :disabled="isCheckboxOptionDisabled(element)"
                                @change="onBenefitCheckboxToggle(element, $event.target.checked)"
                            >
                            <span class="payment-form__option-label">
                                {{ element.options[0]?.label }}
                                <span v-if="Number(element.options?.[0]?.price) > 0" class="payment-form__price-hint">
                                    (+ {{ Number(element.options[0].price).toFixed(2) }} zł)
                                </span>
                            </span>
                        </label>
                        <p v-if="isLimitReached(element.options?.[0])" class="payment-form__warning">
                            Brak wolnych miejsc.
                        </p>

                        <div v-if="isBenefitChecked(element) && element.allows_companion" class="payment-form__companion">
                            <label
                                class="payment-form__option"
                                :class="{ 'payment-form__option--disabled': isCompanionDisabledByLimit(element) }"
                            >
                                <input
                                    class="payment-form__checkbox"
                                    type="checkbox"
                                    :checked="isBenefitCompanionEnabled(element)"
                                    :disabled="isCompanionDisabledByLimit(element)"
                                    @change="onBenefitCompanionToggle(element, $event.target.checked)"
                                >
                                <span class="payment-form__option-label">
                                    Osoba towarzysząca
                                    <span v-if="Number(element.companion_price) > 0" class="payment-form__price-hint">
                                        (+ {{ Number(element.companion_price).toFixed(2) }} zł)
                                    </span>
                                </span>
                            </label>
                            <p v-if="isCompanionDisabledByLimit(element)" class="payment-form__warning">
                                Brak wolnych miejsc.
                            </p>
                            <div v-if="isBenefitCompanionEnabled(element)" class="payment-form__field">
                                <input
                                    class="payment-form__input"
                                    type="text"
                                    placeholder="Imię i nazwisko osoby towarzyszącej"
                                    :value="getBenefitCompanionName(element)"
                                    @input="setBenefitCompanionName(element, $event.target.value)"
                                >
                            </div>
                        </div>
                    </div>

                    <div v-else-if="element.element_type === 'radio'" class="payment-form__benefit-body">
                        <label
                            v-for="option in element.options"
                            :key="`${element.code}-${option.code}`"
                            class="payment-form__option"
                            :class="{ 'payment-form__option--disabled': option.is_effectively_disabled }"
                        >
                            <input
                                class="payment-form__radio"
                                type="radio"
                                :name="`benefit-radio-${element.code}`"
                                :checked="getBenefitOptionCode(element) === option.code"
                                :disabled="option.is_effectively_disabled"
                                @change="setBenefitOptionCode(element, option.code)"
                            >
                            <span class="payment-form__option-label">
                                {{ option.label }}
                                <span v-if="Number(option.price) > 0" class="payment-form__price-hint">
                                    (+ {{ Number(option.price).toFixed(2) }} zł)
                                </span>
                            </span>
                        </label>
                        <p
                            v-for="option in element.options.filter(o => o.is_effectively_disabled && getBenefitOptionCode(element) === o.code)"
                            :key="`warn-${element.code}-${option.code}`"
                            class="payment-form__warning"
                        >
                            {{ optionUnavailabilityMessage(option) }}
                        </p>
                    </div>

                    <div v-else class="payment-form__benefit-body">
                        <div class="payment-form__field">
                            <select
                                class="payment-form__select"
                                :class="{ 'payment-form__select--disabled': !hasSelectableOptions(element) }"
                                :disabled="!hasSelectableOptions(element)"
                                :value="getBenefitOptionCode(element)"
                                @change="setBenefitOptionCode(element, $event.target.value)"
                            >
                                <option disabled value="">Wybierz opcję</option>
                                <option
                                    v-for="option in element.options"
                                    :key="`${element.code}-${option.code}`"
                                    :disabled="option.is_effectively_disabled"
                                    :value="option.code"
                                >
                                    {{ optionDisplayLabel(option) }}
                                </option>
                            </select>
                        </div>
                        <p
                            v-if="!hasSelectableOptions(element) && hasLimitReachedOption(element)"
                            class="payment-form__warning"
                        >
                            Brak wolnych miejsc.
                        </p>
                        <p
                            v-else-if="selectedOptionUnavailabilityMessage(element)"
                            class="payment-form__warning"
                        >
                            {{ selectedOptionUnavailabilityMessage(element) }}
                        </p>

                        <div
                            v-if="element.element_type === 'hotel' && selectedHotelOption(element) && selectedHotelOption(element).requires_companion"
                            class="payment-form__companion"
                        >
                            <div
                                v-for="(name, idx) in getBenefitHotelCompanionNames(element)"
                                :key="`${element.code}-companion-${idx}`"
                                class="payment-form__field"
                            >
                                <input
                                    class="payment-form__input"
                                    type="text"
                                    :placeholder="`Imię i nazwisko współlokatora ${idx + 1}`"
                                    :value="name"
                                    @input="setBenefitHotelCompanionName(element, idx, $event.target.value)"
                                >
                            </div>
                            <p class="payment-form__hint">
                                W przypadku niewskazania współlokatora, dobór do pokoju nastąpi automatycznie
                                (z uwzględnieniem płci).
                            </p>
                        </div>
                    </div>

                    <p v-if="fieldErrors[`benefit.${element.code}`]" class="payment-form__error">
                        {{ fieldErrors[`benefit.${element.code}`] }}
                    </p>
                </div>
                <hr class="payment-form__divider">
            </section>

            <section
                v-if="showPaymentDetails"
                class="payment-form__section payment-form__section--coupon"
            >
                <h3 class="payment-form__section-title">Kod rabatowy</h3>
                <div class="payment-form__field">
                    <input
                        v-model.trim="code"
                        class="payment-form__input"
                        type="text"
                        placeholder="Mam kod rabatowy"
                        @blur="commitCouponCode"
                    >
                </div>
                <p class="payment-form__totals">
                    <span>Cena: <strong>{{ formatAmount(amount) }} zł</strong> brutto</span>
                    <span>Rabat: <strong>{{ formatAmount(discountValue) }} zł</strong> brutto</span>
                </p>

                <div v-if="amount > 0" class="payment-form__methods">
                    <h3 class="payment-form__section-title">
                        Forma płatności <span class="payment-form__required">*</span>
                    </h3>
                    <label class="payment-form__option">
                        <input
                            id="przelewy24"
                            v-model="paymentMethod"
                            class="payment-form__radio"
                            type="radio"
                            value="1"
                            @change="onPaymentMethodChange"
                        >
                        <span class="payment-form__option-label">
                            <img
                                :src="asset_path + '/images/przelewy24_logo.svg'"
                                class="payment-form__p24-logo"
                                alt="Przelewy24"
                                height="36"
                            >
                        </span>
                    </label>
                    <label class="payment-form__option">
                        <input
                            id="przelew"
                            v-model="paymentMethod"
                            class="payment-form__radio"
                            type="radio"
                            value="2"
                            @change="onPaymentMethodChange"
                        >
                        <span class="payment-form__option-label">Przelew bankowy</span>
                    </label>
                    <p v-if="fieldErrors.paymentMethod" class="payment-form__error">
                        {{ fieldErrors.paymentMethod }}
                    </p>
                </div>
            </section>

            <section
                v-if="showPaymentDetails && amount > 0 && !showUserDataEditor"
                class="payment-form__section payment-form__section--invoice"
            >
                <h3 class="payment-form__section-title">Sprawdź poprawność swoich danych</h3>
                <div v-if="loadingUserSummary" class="payment-form__hint">Ładowanie danych...</div>
                <div v-else class="payment-form__user-summary">
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">Imię i nazwisko:</span>
                        <span>{{ fullName() }}</span>
                    </p>
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">Wykonywany zawód:</span>
                        <span>{{ getProfessionLabel(userSummary?.userProfession?.profession) }}</span>
                    </p>
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">{{ professionExtraLabel() }}:</span>
                        <span>{{ professionExtraValue() }}</span>
                    </p>

                    <h4 class="payment-form__subsection-title">Dane do faktury</h4>
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">Typ:</span>
                        <span>{{ userSummary?.company ? 'Firma' : 'Osoba fizyczna' }}</span>
                    </p>
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">Nazwa firmy:</span>
                        <span>{{ userSummary?.company?.name || '-' }}</span>
                    </p>
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">NIP:</span>
                        <span>{{ userSummary?.company?.nip || '-' }}</span>
                    </p>
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">Ulica i numer:</span>
                        <span>{{ invoiceAddress() }}</span>
                    </p>
                    <p class="payment-form__summary-row">
                        <span class="payment-form__summary-label">Kod pocztowy i miasto:</span>
                        <span>{{ invoicePostalCity() }}</span>
                    </p>
                </div>
                <button
                    class="payment-form__submit"
                    type="button"
                    @click="showUserDataEditor = true"
                >
                    Edytuj
                </button>
            </section>

            <section
                v-if="showPaymentDetails && amount > 0 && showUserDataEditor"
                class="payment-form__section payment-form__section--user-editor"
            >
                <user-data
                    ref="userPanel"
                    :hide-update-button="true"
                    :professions="professions"
                    header="Sprawdź swoje dane"
                    redirect=""
                    @dirty-state-changed="onUserDataDirtyChange"
                ></user-data>
                <div class="payment-form__actions">
                    <button
                        class="payment-form__submit"
                        type="button"
                        :disabled="savingUserData"
                        @click="saveUserData"
                    >
                        Zapisz dane
                    </button>
                </div>
            </section>

            <section class="payment-form__section payment-form__section--proof">
                <h3 class="payment-form__section-title">
                    Dokument potwierdzający członkostwo
                </h3>
                <p class="payment-form__hint">
                    Dozwolone formaty: JPG, JPEG, PNG, PDF. Maksymalny rozmiar: 5&nbsp;MB.
                    Przesłanie dokumentu jest obligatoryjne dla członków towarzystwa.
                </p>
                <p class="payment-form__warning">
                    Płatności z uczestnictwem jako członek bez przesłanego potwierdzenia
                    mogą zostać anulowane.
                </p>
                <div class="payment-form__field">
                    <input
                        class="payment-form__file"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                        @change="onMembershipProofSelected"
                    >
                </div>
                <p v-if="membershipProofName" class="payment-form__hint">
                    Wybrany plik: {{ membershipProofName }}
                </p>
                <p v-if="fieldErrors.membershipProof" class="payment-form__error">
                    {{ fieldErrors.membershipProof }}
                </p>
            </section>

            <section
                v-if="showPaymentDetails"
                class="payment-form__section payment-form__section--summary"
            >
                <p class="payment-form__final-price">
                    Cena finalna: <strong>{{ formatAmount(amount) }} zł</strong> brutto
                </p>
                <p v-if="amount > 0" class="payment-form__hint">
                    Faktura zostanie wysłana automatycznie po otrzymaniu płatności.
                </p>
                <p v-if="amount > 0" class="payment-form__confirm-note">
                    Przechodząc do płatności potwierdzasz poprawność swoich danych.
                </p>
            </section>

            <section
                v-if="agreementInputs.length"
                class="payment-form__section payment-form__section--agreements"
            >
                <form-agreement-input
                    v-model="agreements"
                    :agreement-inputs="agreementInputs"
                    :error-messages="errorMessages"
                ></form-agreement-input>
            </section>

            <div class="payment-form__actions">
                <p v-if="userDataDirty" class="payment-form__warning">
                    Zmieniłeś swoje dane. Kliknij Zapisz dane, aby móc przejść do płatności.
                </p>
                <button
                    class="payment-form__submit"
                    type="button"
                    :disabled="isProceedButtonDisabled"
                    @click="handlePaymentForm"
                >
                    <span v-if="amount > 0">Przejdź do płatności</span>
                    <span v-else>Zarejestruj się do wydarzenia</span>
                </button>
            </div>
        </div>

        <div v-if="content" class="payment-form__bank-transfer" v-html="content"></div>
        <notifications group="foo"/>
    </div>
</template>

<script>
import FormAgreementInput from './formAgreementInput';
import UserData from './userData';

export default {
    props: [
        'event_id',
        'price',
        'asset_path',
        'profession',
        'profile_url',
        'register_type',
        'event_type',
        'available_attendance_type',
        'professions',
    ],

    components: {
        FormAgreementInput,
        UserData,
    },

    computed: {
        showMainSections() {
            return !this.content;
        },
        hasEventableBenefits() {
            return !!(this.eventableBenefitsForm?.elements?.length);
        },
        showAttendanceSection() {
            const type = this.event_type || this.available_attendance_type;
            return type === 'hybrid' || this.available_attendance_type === 'hybrid';
        },
        showCouponZeroedSummary() {
            return Number(this.amount) === 0 && (!!this.code || Number(this.discountValue) > 0);
        },
        showPaymentDetails() {
            if (this.amount === null) {
                return false;
            }
            return Number(this.amount) > 0 || this.showCouponZeroedSummary;
        },
        isProceedButtonDisabled() {
            return this.isDisabled || this.userDataDirty || this.savingUserData;
        },
    },

    data() {
        const available = this.available_attendance_type || this.event_type || 'stationary';
        return {
            paymentMethod: null,
            amount: this.price != null ? Number(this.price) : null,
            code: null,
            discountValue: 0,
            content: null,
            agreements: {},
            agreementInputs: [],
            eventableBenefitsForm: null,
            eventableBenefitsData: {},
            isDisabled: false,
            attendanceType: available === 'hybrid' ? 'stationary' : available,
            fieldErrors: {},
            errorMessages: null,
            priceRequestId: 0,
            userDataDirty: false,
            savingUserData: false,
            showUserDataEditor: false,
            userSummary: null,
            loadingUserSummary: false,
            membershipProofFile: null,
            membershipProofName: '',
        };
    },

    watch: {
        attendanceType(newValue, oldValue) {
            if (oldValue !== undefined && newValue && newValue !== oldValue) {
                if (newValue === 'online' || newValue === 'stationary') {
                    this.$delete(this.fieldErrors, 'attendanceType');
                }
                this.fetchEventableBenefits();
                this.setPrice();
            }
        },
    },

    beforeMount() {
        this.setPrice();
        this.fetchUserSummary();

        axios.get('/api/agreements', {
            params: {
                eventableId: this.event_id,
                eventableType: 'App\\Models\\Event',
            },
        }).then((response) => {
            this.agreementInputs = response.data.data;
        });

        this.fetchEventableBenefits();
    },

    methods: {
        fetchUserSummary() {
            this.loadingUserSummary = true;
            return axios.get('/api/user-data')
                .then((response) => {
                    this.userSummary = response.data?.data || null;
                })
                .finally(() => {
                    this.loadingUserSummary = false;
                });
        },
        getProfessionLabel(professionId) {
            try {
                const map = JSON.parse(this.professions || '{}');
                return map?.[professionId] || '-';
            } catch (e) {
                return '-';
            }
        },
        fullName() {
            if (!this.userSummary) {
                return '-';
            }
            return [this.userSummary.name, this.userSummary.lastName].filter(Boolean).join(' ') || '-';
        },
        isStudent() {
            return Number(this.userSummary?.userProfession?.profession) === 4;
        },
        professionExtraLabel() {
            return this.isStudent() ? 'Numer legitymacji' : 'Numer prawa wykonywania zawodu';
        },
        professionExtraValue() {
            if (this.isStudent()) {
                return this.userSummary?.userProfession?.card || '-';
            }
            return this.userSummary?.userProfession?.pwz_number || '-';
        },
        invoiceAddress() {
            const address = this.userSummary?.address || {};
            const parts = [
                address.street,
                address.house_number ? ` ${address.house_number}` : '',
                address.flat_number ? `/${address.flat_number}` : '',
            ].join('');
            return parts.trim() || '-';
        },
        invoicePostalCity() {
            const address = this.userSummary?.address || {};
            const parts = [address.post_code, address.city].filter(Boolean).join(' ');
            return parts || '-';
        },
        async saveUserData() {
            if (!this.$refs.userPanel || !this.$refs.userPanel.handleUpdateForm) {
                return;
            }

            this.savingUserData = true;
            try {
                const updateResult = await this.$refs.userPanel.handleUpdateForm(true);
                if (updateResult !== false) {
                    this.userDataDirty = false;
                    this.showUserDataEditor = false;
                    await this.fetchUserSummary();
                    this.setPrice();
                }
            } catch (e) {
                // validation errors already notified by user-data
            } finally {
                this.savingUserData = false;
            }
        },
        onUserDataDirtyChange(isDirty) {
            this.userDataDirty = isDirty;
        },
        formatAmount(value) {
            const number = Number(value);
            if (Number.isNaN(number)) {
                return '0.00';
            }
            return number.toFixed(2);
        },
        clearFieldErrors() {
            this.fieldErrors = {};
        },
        setFieldError(field, message) {
            this.$set(this.fieldErrors, field, message);
        },
        isEventableElementRequired(element) {
            return ['radio', 'select', 'hotel'].includes(element.element_type) && this.hasSelectableOptions(element);
        },
        hasSelectableOptions(element) {
            return (element.options || []).some((option) => !option.is_effectively_disabled);
        },
        isCheckboxOptionDisabled(element) {
            return !!element.options?.[0]?.is_effectively_disabled;
        },
        isLimitReached(option) {
            return !!option?.is_limit_reached;
        },
        optionUnavailabilityMessage(option) {
            if (!option?.is_limit_reached) {
                return '';
            }
            return 'Brak wolnych miejsc.';
        },
        optionDisplayLabel(option) {
            const priceSuffix = Number(option.price) > 0 ? ` (+ ${Number(option.price).toFixed(2)} zł)` : '';
            const unavailableSuffix = option.is_limit_reached
                ? ` — ${this.optionUnavailabilityMessage(option)}`
                : '';
            return `${option.label}${priceSuffix}${unavailableSuffix}`;
        },
        selectedOptionUnavailabilityMessage(element) {
            const selected = (element.options || []).find((opt) => opt.code === this.getBenefitOptionCode(element));
            return this.optionUnavailabilityMessage(selected);
        },
        isCompanionDisabledByLimit(element) {
            if (!element?.allows_companion || !this.isBenefitChecked(element)) {
                return false;
            }
            const option = element.options?.[0];
            if (!option || option.is_disabled) {
                return false;
            }
            const capacity = option.max_participants_capacity;
            if (capacity === null || capacity === 0) {
                return false;
            }
            const taken = Number(option.taken_slots || 0);
            return (taken + 2) > capacity;
        },
        hasLimitReachedOption(element) {
            return (element.options || []).some((option) => option.is_limit_reached);
        },
        fetchEventableBenefits() {
            axios.get('/api/eventable-benefits', {
                params: {
                    eventableId: this.event_id,
                    eventableType: 'App\\Models\\Event',
                    attendanceType: this.attendanceType,
                },
            }).then((response) => {
                this.eventableBenefitsForm = response.data.data.form;
                this.initializeEventableBenefitsState();
            });
        },
        initializeEventableBenefitsState() {
            const state = {};
            (this.eventableBenefitsForm?.elements || []).forEach((element) => {
                state[element.code] = {
                    element_code: element.code,
                    option_code: '',
                    with_companion: false,
                    companion_names: [],
                };
            });
            this.eventableBenefitsData = state;
        },
        visibleEventableBenefitElements() {
            const elements = this.eventableBenefitsForm?.elements || [];
            const selectedByElement = {};
            const visible = [];

            elements.forEach((element) => {
                const condition = element.visible_when;
                if (!this.isVisibleWhenSatisfied(condition, selectedByElement)) {
                    return;
                }
                visible.push(element);
                selectedByElement[element.code] = this.getBenefitOptionCode(element);
            });

            return visible;
        },
        extractVisibleWhenConditions(condition) {
            if (!condition || typeof condition !== 'object') {
                return [];
            }
            if (Array.isArray(condition.conditions)) {
                return condition.conditions.filter(c => c && c.element_code && c.option_code);
            }
            if (condition.element_code && condition.option_code) {
                return [{ element_code: condition.element_code, option_code: condition.option_code }];
            }
            return [];
        },
        isVisibleWhenSatisfied(condition, selectedByElement) {
            const conditions = this.extractVisibleWhenConditions(condition);
            if (!conditions.length) {
                return true;
            }
            return conditions.some(c => selectedByElement[c.element_code] === c.option_code);
        },
        getBenefitSelection(element) {
            if (!this.eventableBenefitsData[element.code]) {
                this.$set(this.eventableBenefitsData, element.code, {
                    element_code: element.code,
                    option_code: '',
                    with_companion: false,
                    companion_names: [],
                });
            }
            return this.eventableBenefitsData[element.code];
        },
        isBenefitChecked(element) {
            return !!this.getBenefitSelection(element).option_code;
        },
        onBenefitCheckboxToggle(element, checked) {
            if (this.isCheckboxOptionDisabled(element)) {
                return;
            }
            const selection = this.getBenefitSelection(element);
            selection.option_code = checked ? (element.options?.[0]?.code || element.code) : '';
            if (!checked) {
                selection.with_companion = false;
                selection.companion_names = [];
            }
            this.setPrice();
        },
        isBenefitCompanionEnabled(element) {
            return !!this.getBenefitSelection(element).with_companion;
        },
        onBenefitCompanionToggle(element, enabled) {
            if (this.isCompanionDisabledByLimit(element)) {
                return;
            }
            const selection = this.getBenefitSelection(element);
            selection.with_companion = enabled;
            if (!enabled) {
                selection.companion_names = [];
            } else if (!selection.companion_names.length) {
                selection.companion_names = [''];
            }
            this.setPrice();
        },
        getBenefitCompanionName(element) {
            return this.getBenefitSelection(element).companion_names?.[0] || '';
        },
        setBenefitCompanionName(element, value) {
            this.getBenefitSelection(element).companion_names = [value];
        },
        getBenefitOptionCode(element) {
            return this.getBenefitSelection(element).option_code || '';
        },
        setBenefitOptionCode(element, optionCode) {
            if (!this.hasSelectableOptions(element)) {
                return;
            }
            const selection = this.getBenefitSelection(element);
            selection.option_code = optionCode;
            this.$delete(this.fieldErrors, `benefit.${element.code}`);
            if (element.element_type === 'hotel') {
                const selected = this.selectedHotelOption(element);
                const count = selected && selected.requires_companion
                    ? Math.max(0, Number(selected.beds_count) - 1)
                    : 0;
                selection.companion_names = Array.from(
                    { length: count },
                    (_, idx) => selection.companion_names[idx] || '',
                );
            }
            this.setPrice();
        },
        selectedHotelOption(element) {
            return (element.options || []).find((opt) => opt.code === this.getBenefitOptionCode(element));
        },
        getBenefitHotelCompanionNames(element) {
            return this.getBenefitSelection(element).companion_names || [];
        },
        setBenefitHotelCompanionName(element, index, value) {
            const selection = this.getBenefitSelection(element);
            const names = [...(selection.companion_names || [])];
            names[index] = value;
            selection.companion_names = names;
        },
        buildEventableBenefitsPayload() {
            const payload = [];
            this.visibleEventableBenefitElements().forEach((element) => {
                const selection = this.getBenefitSelection(element);
                if (element.element_type === 'checkbox') {
                    if (!selection.option_code) {
                        return;
                    }
                    payload.push({
                        element_code: element.code,
                        option_code: selection.option_code,
                        with_companion: !!selection.with_companion,
                        companion_names: selection.companion_names || [],
                    });
                    return;
                }
                if (!selection.option_code) {
                    return;
                }
                payload.push({
                    element_code: element.code,
                    option_code: selection.option_code,
                    companion_names: selection.companion_names || [],
                });
            });
            return payload;
        },
        validateEventableBenefitsBeforeSubmit() {
            for (const element of this.visibleEventableBenefitElements()) {
                if (element.element_type === 'checkbox') {
                    continue;
                }
                if (!this.hasSelectableOptions(element)) {
                    continue;
                }
                if (!this.getBenefitOptionCode(element)) {
                    this.setFieldError(`benefit.${element.code}`, 'To pole jest wymagane.');
                    this.$notify({
                        group: 'foo',
                        type: 'error',
                        title: `Prosimy wybrać opcję dla pola: ${element.label || element.code}`,
                    });
                    return false;
                }
            }
            return true;
        },
        commitCouponCode(event) {
            const normalized = (event?.target?.value ?? this.code ?? '').toString().trim();
            this.code = normalized || null;
            this.setPrice();
        },
        onPaymentMethodChange() {
            this.$delete(this.fieldErrors, 'paymentMethod');
            this.setPrice();
        },
        onMembershipProofSelected(event) {
            this.$delete(this.fieldErrors, 'membershipProof');
            const file = event?.target?.files?.[0] || null;
            if (!file) {
                this.membershipProofFile = null;
                this.membershipProofName = '';
                return;
            }
            const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
            const extOk = /\.(jpe?g|png|pdf)$/i.test(file.name || '');
            if ((!allowed.includes(file.type) && !extOk) || file.size > 5 * 1024 * 1024) {
                this.membershipProofFile = null;
                this.membershipProofName = '';
                event.target.value = '';
                this.setFieldError(
                    'membershipProof',
                    'Dozwolone są pliki JPG, PNG lub PDF o rozmiarze do 5 MB.',
                );
                return;
            }
            this.membershipProofFile = file;
            this.membershipProofName = file.name;
        },
        buildMembershipProofSummary() {
            return this.visibleEventableBenefitElements().map((element) => {
                const optionCode = this.getBenefitOptionCode(element);
                const option = (element.options || []).find(o => o.code === optionCode);
                const label = (element.label || element.code || '').replace(/<[^>]+>/g, '');
                const optionLabel = option?.label || optionCode || '—';
                return `${label}: ${optionLabel}`;
            }).join('\n');
        },
        sendMembershipProofRequest() {
            if (!this.membershipProofFile) {
                return Promise.resolve();
            }
            const formData = new FormData();
            formData.append('document', this.membershipProofFile);
            formData.append('benefits_summary', this.buildMembershipProofSummary());
            return axios.post('/api/payment/membership-proof', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        setPrice(forceNoCoupon = false) {
            const requestId = ++this.priceRequestId;
            const couponCode = forceNoCoupon ? null : (this.code || null);
            axios.post('/api/prices', {
                eventableId: this.event_id,
                eventableType: 'App\\Models\\Event',
                eventableBenefitsData: this.buildEventableBenefitsPayload(),
                code: couponCode,
                attendanceType: this.attendanceType,
            }).then((response) => {
                if (requestId !== this.priceRequestId) {
                    return;
                }
                this.amount = response.data.data.price;
                this.discountValue = response.data.data.discountValue;
            }).catch((error) => {
                if (requestId !== this.priceRequestId) {
                    return;
                }
                this.discountValue = 0;
                const status = error?.response?.status;
                if (status === 422) {
                    const message = error.response?.data?.message
                        || error.response?.data?.errors?.code?.[0];
                    if (!forceNoCoupon && typeof message === 'string' && message.length > 0) {
                        this.code = null;
                        this.$notify({
                            group: 'foo',
                            type: 'error',
                            title: message,
                        });
                        this.$nextTick(() => this.setPrice(true));
                    }
                    return;
                }
                if (error.response?.data?.errors || error.response?.data?.message) {
                    const message = error.response.data.message
                        || error.response.data.errors?.code?.[0];
                    this.$notify({
                        group: 'foo',
                        type: 'error',
                        title: message,
                    });
                }
            });
        },
        handlePaymentForm() {
            this.isDisabled = true;
            this.clearFieldErrors();

            if (this.showAttendanceSection
                && this.attendanceType !== 'stationary'
                && this.attendanceType !== 'online'
            ) {
                this.setFieldError('attendanceType', 'Wybierz formę udziału.');
                this.$notify({
                    group: 'foo',
                    type: 'error',
                    title: 'Prosimy wybrać formę udziału',
                });
                this.isDisabled = false;
                return false;
            }

            if (!this.showAttendanceSection
                && this.available_attendance_type
                && this.available_attendance_type !== 'hybrid'
                && this.available_attendance_type !== this.attendanceType
            ) {
                this.attendanceType = this.available_attendance_type;
            }

            if (this.paymentMethod === null && Number(this.amount) !== 0) {
                this.setFieldError('paymentMethod', 'Wybierz formę płatności.');
                this.$notify({
                    group: 'foo',
                    type: 'error',
                    title: 'Prosimy wybrać metodę płatności',
                });
                this.isDisabled = false;
                return false;
            }

            if (this.validateEventableBenefitsBeforeSubmit() === false) {
                this.isDisabled = false;
                return false;
            }

            const createPayment = () => axios.post('/api/payment/create', {
                paymentMethod: this.paymentMethod,
                code: this.code,
                eventableId: this.event_id,
                eventableType: 'App\\Models\\Event',
                amount: this.amount,
                agreements: this.agreements,
                eventableBenefitsData: this.buildEventableBenefitsPayload(),
                attendanceType: this.attendanceType,
            });

            const proofPromise = this.membershipProofFile
                ? this.sendMembershipProofRequest().catch((error) => {
                    const message = error.response?.data?.message
                        || error.response?.data?.errors?.document?.[0]
                        || 'Nie udało się wysłać dokumentu potwierdzającego.';
                    this.$notify({ group: 'foo', type: 'error', title: message });
                    throw error;
                })
                : Promise.resolve();

            proofPromise.then(() => createPayment()).then((response) => {
                const data = response.data.data;
                this.errorMessages = null;
                if (data.redirect_panel) {
                    this.$notify({
                        group: 'foo',
                        type: 'success',
                        title: response.data.message,
                    });
                    setTimeout(() => {
                        window.location.href = '/profil';
                    }, 1500);
                    return;
                }
                if (data.url) {
                    this.$notify({
                        group: 'foo',
                        type: 'success',
                        title: response.data.message,
                    });
                    setTimeout(() => {
                        window.location.href = data.url;
                    }, 1200);
                    return;
                }
                if (data.content) {
                    this.content = data.content;
                    this.$notify({
                        group: 'foo',
                        type: 'success',
                        title: response.data.message,
                    });
                    this.$nextTick(() => {
                        const target = this.$el?.querySelector('.payment-form__bank-transfer') || this.$el;
                        if (!target) {
                            return;
                        }
                        const top = target.getBoundingClientRect().top + window.scrollY;
                        const navOffset = 100;
                        window.scrollTo({
                            top: Math.max(top - navOffset, 0),
                            behavior: 'smooth',
                        });
                    });
                }
            }).catch((error) => {
                this.isDisabled = false;
                const errors = error.response?.data?.errors;
                if (errors?.nip && this.$refs.userPanel) {
                    this.showUserDataEditor = true;
                    this.$refs.userPanel.errorMessages = errors;
                    this.$nextTick(() => {
                        const errorEl = document.querySelector('.error');
                        if (errorEl?.offsetParent) {
                            window.scrollTo(0, errorEl.offsetParent.offsetTop);
                        }
                    });
                }
                if (errors) {
                    this.errorMessages = errors;
                    this.$functions.catchApiErrors(error, this);
                    return;
                }
                if (error.response?.data?.message) {
                    this.$notify({
                        group: 'foo',
                        type: 'error',
                        title: error.response.data.message,
                    });
                }
            });
        },
    },
};
</script>
